import { useState, MouseEvent, SyntheticEvent, useContext } from 'react';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { CircularProgress, IconButton, Menu, MenuItem } from '@mui/material';
import { ClickAwayListenerProps } from '@mui/material/ClickAwayListener';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import DeleteIcon from 'src/assets/icons/Actions/DeleteIcon';
import UploadLabelIcon from 'src/assets/icons/File/UploadLabelIcon';
import { AssetModification } from 'src/models/assets/asset-modification';

import { authStoreCtx } from '../../../../stores/auth';

interface Props {
  onClose?: () => void;
  downloadPending: boolean;
  deletePending: boolean;
  fileId: number;
  chosenAsset: AssetModification | undefined;
  onDeletion?: () => void;
  handlePreview?: (args: number) => void;
}

export default ({
  onClose,
  downloadPending,
  deletePending,
  fileId,
  handlePreview,
  chosenAsset,
  onDeletion,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { fCfg } = useContext(authStoreCtx);

  if (!chosenAsset) return null;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    event.stopPropagation();
    event.preventDefault();
  };

  const handleClose = (event: MouseEvent<HTMLElement>) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    onClose && onClose();
    setAnchorEl(null);
  };

  const handleClickAway: ClickAwayListenerProps['onClickAway'] = event => {
    event.preventDefault();
    event.stopPropagation();
    handleClose(event as any);
  };

  const previewHandling = (event: MouseEvent<HTMLElement>) => {
    handlePreview && handlePreview(fileId);
    setAnchorEl(null);
    event.stopPropagation();
    event.preventDefault();
  };

  const handleAction = (event: SyntheticEvent) => {
    if (event.currentTarget?.id.includes('download')) {
      return chosenAsset.attachments.download(fileId);
    }
    if (event.currentTarget?.id.includes('delete')) {
      (async () => {
        await chosenAsset.attachments.deleteFile(fileId);
        !!onDeletion && (await onDeletion());
      })();
    }
  };

  return (
    <div>
      <IconButton
        aria-label="mores"
        id="file-buttons"
        aria-controls={open ? 'menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      {open && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <Menu
            id="long-menu"
            MenuListProps={{
              'aria-labelledby': 'button',
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            disableScrollLock
            onClick={event => event.stopPropagation()}>
            <MenuItem id="download" onClick={handleAction}>
              {downloadPending ? (
                <CircularProgress size={20} />
              ) : (
                <UploadLabelIcon sx={{ p: theme => theme.spacing(0.5) }} />
              )}
              Download
            </MenuItem>
            {fCfg.isFilePreviewCarousel && (
              <MenuItem id="preview" onClick={previewHandling}>
                <RemoveRedEyeIcon sx={{ pr: theme => theme.spacing(0.3) }} />
                Preview
              </MenuItem>
            )}
            <MenuItem
              disabled={
                !chosenAsset?.canUpdate || !chosenAsset.isRelatedEntityActive
              }
              id="delete"
              onClick={handleAction}>
              {deletePending ? (
                <CircularProgress size={20} />
              ) : (
                <DeleteIcon sx={{ p: theme => theme.spacing(0.5) }} />
              )}
              Delete
            </MenuItem>
          </Menu>
        </ClickAwayListener>
      )}
    </div>
  );
};
